import React, {useState, useEffect, useCallback, useMemo, useContext} from 'react';
import Button from '../common/components/Button';
import NoContent from '../common/components/NoContent';
import AddCategory, { newCategoryType } from './AddCategoryPopup';
import NoCategoryIcon from '../../assests/img/no-role.svg?react';
import {addNewCategoryApi} from '../common/api/postApi';
import {toast} from 'react-toastify';
import {getDataApi} from '../common/api/getApiData';
import {listItemPerPage} from '../../constants/options';
import ContentHeader from '../common/components/ContentHeader';
import Checkbox from '../common/components/Checkbox';
import SortBy, {sortByType} from '../common/components/SortBy';
import DeleteCategory from './DeleteCategoryPopup';
import Loader from '../common/components/Loader';
import Pagination from '../common/components/Pagination';
import {convertDate, getUserPermisson} from '../common/functions/utils';
import SettingsIcon from '../../assests/img/settings-icon.svg?react';
import CategoryManageMenu, {categoryMenuItemIdType} from './CategoryManageMenu';
import {deleteDataApi} from '../common/api/deleteApi';
import {updateCategoryApi} from '../common/api/updateApi';
import _ from 'lodash';
import AddIcon from '../../assests/img/add-icon.svg?url';
import {uploadFile} from '../common/functions/handleFiles';
import {UserContext} from '../common/context/userContext';
import {AuthContext} from '../common/context/authContext';


export interface categoryType {
    categoryId: string,
    categoryName: string,
    fileUrl: string,
    fileUploaded: any,
    createdById: string,
    createdByName: string,
    updatedById: string,
    updatedByName: string,
    createdOn: string,
    updatedOn: string,
}

type columnSortId = 'categoryName' | 'createdOn' | 'createdByName';


function CategoryContent() {
    const {data: {permissions}} = useContext(UserContext);
    const [
        categories,
        setCategories,
    ] = useState<categoryType[]>([
    ]);
    const [
        addPopup,
        setAddPopup,
    ] = useState<boolean>(false);
    const [
        loading,
        setLoading,
    ] = useState<boolean>(true);
    const [
        currentPage,
        setCurrentPage,
    ] = useState<number>(1);
    const [
        showMenuContent,
        setShowMenuContent,
    ] = useState<boolean>(false);
    const [
        menuId,
        setMenuId,
    ] = useState<categoryMenuItemIdType>('None');
    const [
        showMenuItemPopup,
        setShowMenuItemPopup,
    ] = useState<boolean>(false);
    const [
        sortByValue,
        setSortByValue,
    ] = useState<{[key: string]: sortByType}>({
        categoryName: 'default',
        createdOn: 'default',
    });
    const [
        selectedCategory,
        setSelectedCategory,
    ] = useState<categoryType>({
        categoryId: '',
        categoryName: '',
        fileUrl: '',
        fileUploaded: null,
        createdById: '',
        createdByName: '',
        updatedById: '',
        updatedByName: '',
        createdOn: '',
        updatedOn: '',
    });

    const {accounts} = useContext(AuthContext);

    const getCategories = useCallback(async (
        orderByStr?: string, search?: string,
    ) => {
        setLoading(true);
        const filter = (
            orderByStr && search ?
            `?${orderByStr}&SearchQuery=${search}` : orderByStr ? `?${orderByStr}` : search ? `?SearchQuery=${search}` : '');
        const value = await getDataApi(`GetCategory${filter}`, accounts)
        setCategories(value);
        setLoading(false);
    }, [
    ]);

    const getCategorySearch = useMemo(()=> _.debounce(getCategories, 700), [
    ]);

    useEffect(() => {
        const orderBy = Object.keys(sortByValue).filter((key) => {
            return sortByValue[key] !== 'default'; 
        }).map((key1) => {
            return `${key1}:${sortByValue[key1] === 'asc' ? 1 : 2}`;
        });
        let orderByStr = `${orderBy.join(',')}`;
        orderByStr = orderByStr ? `CommaSepColumn=${orderByStr}`: '';
        getCategories(orderByStr);
    }, [
        sortByValue, currentPage, getCategories,
    ]);

    const handleSearch = (search: string) => {
        getCategorySearch('', search);
    };

    const getTotalPages = () =>
        categories.length<=listItemPerPage ? 1 :
    Math.ceil(categories.length / listItemPerPage);

    const getStartEndIndices = () => {
        const start = currentPage === 1? 0:
        (currentPage-1) * listItemPerPage;
        const end = currentPage === 1?
        listItemPerPage: start + (listItemPerPage);
        return [start, end];
    }

    const paginatedUserData = (category: categoryType[]) => {
        if (category.length<=6) {
            return category;
        } else {
            const [start, end] = getStartEndIndices();
            const cardDataPaginated = category.slice(start, end);
            return cardDataPaginated;
        }
    }

    const handleAddCategory = async (newCategory: newCategoryType) => {
        try {
            const {fileUrl, res, error} = await toast.promise(
                    uploadFile(newCategory.uploadedImg, 'categories'),
                {pending: `Uploading file - ${newCategory.uploadedImg.name}`});
            if (!error && res?._response.status === 201) {
                await addNewCategoryApi({...newCategory, fileUrl}, accounts);
                toast.success('Category created successfully');
                setAddPopup(false);
                getCategories();
                return true;
            } else {
                toast.error(error);
                return false;
            }
        } catch (e: any) {
            toast.error(e.message);
            return false;
        }
    };

    const handleEditCategory = async (category: categoryType) => {
        if (category.fileUploaded) {
            const {fileUrl, res, error} = await toast.promise(
                uploadFile(category.fileUploaded, 'categories'),
            {pending: `Uploading file - ${category.fileUploaded.name}`});
            if (!error && res?._response.status === 201) {
                const res = await updateCategoryApi(`Category/categoryId/${category.categoryId}`, {categoryName: category.categoryName, fileUrl}, accounts);
                if (res.res === 200) {
                    toast.success('Category updated successfully');
                    setShowMenuItemPopup(false);
                    getCategories();
                    return true;
                } else {
                    toast.error(res.error);
                    return false;
                }
            } else {
                toast.error(error);
                return false;
            }
        } else {
            const {res, error} = await updateCategoryApi(`Category/categoryId/${category.categoryId}`, {categoryName: category.categoryName}, accounts);
            if (res === 200) {
                toast.success('Category updated successfully');
                setShowMenuItemPopup(false);
                getCategories();
                return true;
            } else {
                toast.error(error);
                return false;
            }
        }
    };

    const handleDeleteCategory = async (categoryId: string) => {
        const {res} = await deleteDataApi(`Category/categoryId/${categoryId}`,
            accounts);
        if (res === 204) {
            toast.success('Category deleted successfully');
            setShowMenuItemPopup(false);
            getCategories();
        } else {
            setShowMenuItemPopup(false);
            toast.error(res.data.error.message);
        }
    };

    const handleMenuItemClick = (menuId: categoryMenuItemIdType) => {
        setShowMenuContent(true);
        setMenuId(menuId);
        if (menuId !== 'None') {
            setShowMenuItemPopup(true);
        }
    };

    const handleMenuClick = (categoryId: string) => {
        const matchedCategory = categories.filter((category) =>
            category.categoryId === categoryId)[0];
        setSelectedCategory(matchedCategory);
    };

    const handleSort = (sortType: sortByType, columnId: columnSortId) => {
        setSortByValue((prevState) => {
            return {
                ...prevState,
                [columnId]: sortType,
            };
        });
    };

    const handleClearSort = (columnSortId: columnSortId) => {
        setSortByValue((prevState) => {
            return {
                ...prevState,
                [columnSortId]: 'default',
            };
        });
    };

    const renderOtherPopup = () => {
        switch(menuId) {
            case 'Edit':
                return (<AddCategory
                    setShowPopup={(show) => setShowMenuItemPopup(show)}
                    showPopup={showMenuItemPopup}
                    isEditable={showMenuItemPopup}
                    categoryToEdit={selectedCategory}
                    addCategory={handleAddCategory}
                    editCategory={handleEditCategory}
                />);
            case 'Delete':
                return (<DeleteCategory
                    showPopup={showMenuItemPopup}
                    setShowPopup={(show) => setShowMenuItemPopup(show)}
                    deleteCategory={() =>
                        handleDeleteCategory(selectedCategory.categoryId)}
                />);
        }
    };

    const paginedData = paginatedUserData(categories);

    return (
        <>
            <ContentHeader
                title='All Categories'
                count={categories.length}
                customClass='manage-user-list-content-header'
                showDownload={false}
                showSort={false}
                onSearch={handleSearch}
                showWholesalers={false}
                onWholesalersSelect={()=> null}
                showAdd={getUserPermisson(permissions, 'A')}
                ctaProps={{
                    buttonText: 'Add',
                    onClick: () => setAddPopup(true),
                    type: 'primary-outline',
                    buttonIconUrl: AddIcon,
                }}/>
            <AddCategory
                setShowPopup={(value) => setAddPopup(value)}
                showPopup={addPopup}
                addCategory={handleAddCategory}/>
            {showMenuContent && renderOtherPopup()}
            {!loading ? paginedData.length ?
            <div className='content-container-box-shadow'>
                <div className='content-container-scrollable markex-scroll'>
                    <table className='manage-user-list category-list'>
                        <tbody>
                        <tr>
                            <td className='manage-user-list-header'><span className='d-flex align-items-center gap-2'>
                            <Checkbox onClick={(checked) => checked} 
                            selected={false}/>
                            <SortBy sortType={sortByValue.categoryName} onClick={(sortby) => handleSort(sortby, 'categoryName')} onClear={() => handleClearSort('categoryName')}/> Name</span></td>
                            <td className='manage-user-list-header'><span> Preview</span></td>
                            <td className='manage-user-list-header'><span className='d-flex align-items-center gap-2'><SortBy sortType={sortByValue.createdOn} onClick={(sortby) => handleSort(sortby, 'createdOn')} onClear={() => handleClearSort('createdOn')}/> Created On</span></td>
                            <td className='manage-user-list-header'><span className='d-flex align-items-center gap-2'><SortBy sortType={sortByValue.createdByName} onClick={(sortby) => handleSort(sortby, 'createdByName')} onClear={() => handleClearSort('createdByName')}/> Created By</span></td>
                        </tr>
                        {paginedData.map((category) => {
                            return (
                                <tr key={`category-id-${category.categoryId}`}>
                                    <td className='manage-user-list-cell'>
                                        <span className='d-flex align-items-center gap-1'>
                                            <Checkbox
                                                onClick={(checked) =>
                                                    checked}
                                                selected={
                                                    selectedCategory.categoryId 
                                                    === category.categoryId}/>
                                            <span>
                                                {category.categoryName}
                                            </span>
                                        </span>
                                    </td>
                                    <td className='manage-user-list-cell'>
                                        <div className='d-flex align-items-center justify-content-center'>
                                            <img
                                                src={category.fileUrl}
                                                width={24} height={24}></img>
                                        </div>
                                    </td>
                                    <td className='manage-user-list-cell'>
                                    {convertDate(category.createdOn)}
                                    </td>
                                    <td className='manage-user-list-cell'>
                                    <span className='d-flex align-items-center justify-content-between'>
                                            <span>
                                            {category.createdByName}</span>
                                            <CategoryManageMenu
                                            targetElement={<SettingsIcon/>} 
                                            targetId={category.categoryId}
                                            onClickMenuItem={
                                                handleMenuItemClick}
                                            onClick={(show, id) =>
                                            handleMenuClick(id)}/>
                                        </span>
                                        
                                    </td>
                                </tr>
                            )
                    })}</tbody></table>
                </div>
                <Pagination
                    totalPages={getTotalPages()}
                    currentPage={currentPage}
                    pageStatusText={`Showing ${listItemPerPage} items per page of ${categories.length} items`}
                    setCurrentPage={(page) => setCurrentPage(page)} customClassName='content-container-pagination'
                />
            </div> :
            <NoContent
                textToDisplay={'You don’t have any categories created. Create your first category'}
                cta={<Button buttonText='Create Category' type='primary' onClick={() => setAddPopup(true)}/>}
                mainImage={<NoCategoryIcon width={"185"} height={"141"}/>}
            /> :
            <Loader/>
            }
        </>
    );

}

export default CategoryContent;
